import * as Yup from "yup";

import { ADMIN_ROLE, LANDLORD_ROLE, RENTER_ROLE, ROLES, SUPERADMIN_ROLE } from "../../store/User";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TableHead,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { FieldArray, Form, Formik } from "formik";
import { useEffect, memo, useCallback, useContext, useMemo, useState } from "react";
import { RestrictButton, RestrictIconButton } from "../RestrictedComponents";
import {
  CheckboxField,
  GeneratePassword,
  Section,
  SelectField,
  SubmitButton,
  TextField,
} from "@microrealestate/commonui/components";

import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { observer } from "mobx-react-lite";
import PersonIcon from "@material-ui/icons/Person";
import { StoreContext } from "../../store";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import useConfirmDialog from "../ConfirmDialog";
import useTranslation from "next-translate/useTranslation";
import WarningIcon from "@material-ui/icons/Warning";
import { AtSign, EditIcon } from "lucide-react";
import useEditMemberDialog from "../../components/settings/EditMembers";
import useEditLandlordDialog from "../../components/settings/EditLandlord";
import { property } from "lodash";
import { useRouter } from "next/router";
import Hidden from "../HiddenSSRCompatible";
import {
  AccountCircle,
  Add,
  Block,
  CheckCircle,
  Delete,
  Edit,
  ExpandMore,
  RemoveCircle,
  RotateLeft,
  SupervisedUserCircle,
} from "@material-ui/icons";
import { PrimaryCard } from "../Cards";

const initialValues = {
  fullName: "",
  email: "",
  role: ADMIN_ROLE,
  scopeProperty: [""],
};

function MemberItem({ member, memberIndex, onRemove, onDisable, onEnable, onSend, onEditLandlord, onEditAdmin }) {
  const { t } = useTranslation("common");

  const handleClickRemove = useCallback(() => {
    onRemove?.(member);
  }, [member, onRemove]);

  const handleClickEnable = useCallback(() => {
    onEnable?.(member);
  }, [member, onEnable]);

  const handleClickDisable = useCallback(() => {
    onDisable?.(member);
  }, [member, onDisable]);

  const handleClickSend = useCallback(() => {
    onSend?.(member);
  }, [member, onSend]);

  const handleClickEdit = useCallback(() => {
    if (member.role === "landlord") {
      onEditLandlord?.(member);
    } else {
      onEditAdmin?.(member);
    }
  }, [member, onEditLandlord, onEditAdmin]);

  return (
    <Box style={{ padding: 10 }}>
      {/* Icon + Profile */}
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
        {member.role === "landlord" ? (
          <AccountCircle style={{ width: 60, height: 60 }} color="default" />
        ) : (
          <SupervisedUserCircle style={{ width: 60, height: 60 }} />
        )}
        <Typography variant="h6" align="center">
          {member.fullName}
        </Typography>
        <Divider wid />
        <Typography variant="caption">{member.email}</Typography>
        <Typography variant="caption">{member.role}</Typography>
        <Typography variant="caption">
          {member.role === "administrator"
            ? member.scopeProperty.map((property, index) => property.name)
            : t("All property")}
        </Typography>
      </Box>
      {/* Detail */}
      <Grid container spacing={1} justifyContent="center" alignItems="center">
        <Grid item>
          <Tooltip title={t("Edit")}>
            <IconButton onClick={handleClickEdit}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
        {member.role !== "landlord" ? (
          <Grid item>
            <Tooltip title={t("Delete")}>
              <IconButton onClick={handleClickRemove}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        ) : null}
        <Grid item>
          <Tooltip title={t("Reset Password")}>
            <IconButton onClick={handleClickSend}>
              <RotateLeft fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
        {member.role !== "landlord" ? (
          <Grid item>
            {member.isEnabled ? (
              <Tooltip title={t("Disable")}>
                <IconButton onClick={handleClickDisable}>
                  <Block fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={t("Enable")}>
                <IconButton onClick={handleClickEnable}>
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
}

function MemberGrid({ members, onRemove, onDisable, onEnable, onSend, onEditLandlord, onEditAdmin }) {
  return (
    <Grid container spacing={3}>
      {members.map((member, index) => {
        return (
          <Grid key={member._id} item xs={12} sm={6} lg={3}>
            <PrimaryCard bgColor={member.isEnabled ? "inherit" : "#e0e0e0"} style={{ border: "1px solid #e0e0e0" }}>
              <MemberItem
                member={member}
                memberIndex={index}
                onRemove={onRemove}
                onDisable={onDisable}
                onEnable={onEnable}
                onSend={onSend}
                onEditLandlord={onEditLandlord}
                onEditAdmin={onEditAdmin}
              />
            </PrimaryCard>
          </Grid>
        );
      })}
    </Grid>
  );
}

const FormDialog = memo(function FormDialog({ members = [], onSubmit }) {
  const store = useContext(StoreContext);
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [roleValues, setRoleValues] = useState([]);

  const handleClickOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const _onSubmit = useCallback(
    async (member) => {
      const realmId = store.organization.selected._id;
      let members;

      if (member.role === ADMIN_ROLE) {
        members = {
          ...member,
          realmId: realmId,
        };
      } else {
        members = {
          ...member,
          realmId: realmId,
          scopeProperty: ["fullAccess"],
        };
      }
      await onSubmit(members);

      handleClose();
    },
    [handleClose, onSubmit, store.organization.selected._id],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        fullName: Yup.string().required(),
        email: Yup.string()
          .email()
          .notOneOf(
            members.map(({ email }) => email),
            t("Email has been used"),
          )
          .required(t("Name is a required field")),
        role: Yup.string().required(t("Role is a required field")),
        scopeProperty: Yup.array().when("role", {
          is: (role) => role !== LANDLORD_ROLE && role !== SUPERADMIN_ROLE,
          then: Yup.array().of(Yup.string().required(t("Property is a required field"))),
          otherwise: Yup.array(),
        }),
      }),
    [members, t],
  );

  useEffect(() => {
    async function fetchRoles() {
      let roles = (await store.rbac?.getRoles?.()) || [];
      roles = roles.filter((role) => role.key !== LANDLORD_ROLE);
      setRoleValues(
        roles.map((role) => ({
          id: role._id,
          label: t(role.name),
          value: role.key,
        })),
      );
    }
    fetchRoles();
  }, [store.rbac, t]);

  const availableProperties = useMemo(
    () => store.property.items.map((property) => ({ id: property._id, label: property.name, value: property._id })),
    [store.property.items],
  );

  const handleRemoveProperty = useCallback(
    (index) => {
      const newSelectedProperties = [...selectedProperties];
      newSelectedProperties.splice(index, 1);
      setSelectedProperties(newSelectedProperties);
    },
    [selectedProperties],
  );

  return (
    <>
      <RestrictButton
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleClickOpen}
        className="shepherd-setting-addAdmin"
      >
        {t("New Member")}
      </RestrictButton>
      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={_onSubmit}>
          {({ isSubmitting, values, setFieldValue }) => (
            <Form autoComplete="off">
              <DialogTitle id="form-dialog-title">{t("New team member")}</DialogTitle>
              <DialogContent>
                <DialogContentText>{t("Add a new team member to your organization")}</DialogContentText>
                <Box minHeight={100} minWidth={500}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField label={t("Name")} name="fullName" />
                    </Grid>
                    <Grid item xs={8}>
                      <TextField label={t("Email")} name="email" />
                    </Grid>
                    <Grid item xs={4}>
                      <SelectField label={t("Role")} name="role" values={roleValues} />
                    </Grid>
                    {values.role !== SUPERADMIN_ROLE && values.role !== LANDLORD_ROLE ? (
                      <FieldArray name="scopeProperty">
                        {({ push, remove }) => (
                          <>
                            {values.scopeProperty?.map((property, index) => (
                              <Grid container spacing={2} key={index}>
                                <Grid item xs={8}>
                                  <SelectField
                                    label={t("Property")}
                                    name={`scopeProperty.${index}`}
                                    values={availableProperties}
                                  />
                                </Grid>
                                {index !== 0 && (
                                  <Grid item xs={4}>
                                    <Button
                                      onClick={() => {
                                        remove(index);
                                        handleRemoveProperty(index);
                                      }}
                                    >
                                      <DeleteIcon />
                                    </Button>
                                  </Grid>
                                )}
                              </Grid>
                            ))}
                            {values.scopeProperty.length < availableProperties.length && (
                              <Grid item xs={12}>
                                <Button
                                  onClick={() => {
                                    push("");
                                  }}
                                >
                                  {t("Add property")}
                                </Button>
                              </Grid>
                            )}
                          </>
                        )}
                      </FieldArray>
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="caption" style={{ color: "rgba(0, 0, 0, 0.54)" }}>
                          {t("Property")}
                        </Typography>
                        <Typography>{t("All property")}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>{t("Cancel")}</Button>
                <SubmitButton label={!isSubmitting ? t("Add") : t("Submitting")} />
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
});

const Members = observer(({ memberList, onSubmit }) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const store = useContext(StoreContext);

  const [EditMemberDialog, setOpenEditMemberDialog] = useEditMemberDialog();
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  // const [ConfirmDialogDisable, setMemberToDisable, memberToDisable] = useConfirmDialog();
  // const [ConfirmDialogEnable, setMemberToEnable, memberToEnable] = useConfirmDialog();
  const [ConfirmDialogRemove, setRemoveMember, removeMember] = useConfirmDialog();
  const [ConfirmRemoveDialog, setMemberToRemove, memberToRemove] = useConfirmDialog();
  const [ConfirmEnableDialog, setMemberToEnable, memberToEnable] = useConfirmDialog();
  const [ConfirmDisableDialog, setMemberToDisable, memberToDisable] = useConfirmDialog();
  const [ConfirmSendDialog, setMemberToSend, memberToSend] = useConfirmDialog();
  const [updatedMembers, setUpdatedMembers] = useState([]);
  const [EditLandlordDialog, setLandlordToEdit, landlordToEdit] = useEditLandlordDialog();
  const [roleList, setRoleList] = useState({});

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const updatedMembersCopy = [];

        for (const member of memberList) {
          const updatedScopeProperty = [];

          for (const propertyId of member.scopeProperty || []) {
            if (propertyId === "fullAccess") {
              updatedScopeProperty.push({ id: propertyId, name: "Full Access" });
            } else {
              const propertyData = await store.property.fetchOne(propertyId);
              updatedScopeProperty.push({ id: propertyId, name: propertyData.data.name });
            }
          }
          const updatedMember = { ...member, scopeProperty: updatedScopeProperty };
          updatedMembersCopy.push(updatedMember);
        }
        setUpdatedMembers(updatedMembersCopy);
      } catch (error) {
        console.error(error);
      }
    };

    fetchProperties();
  }, [memberList, store.organization.selected._id, store.property]);

  useEffect(() => {
    async function fetchRoles() {
      let roles = (await store.rbac?.getRoles?.()) || [];
      setRoleList(
        roles.reduce((acc, role) => {
          acc[role.key] = role.name;
          return acc;
        }, {}),
      );
    }
    fetchRoles();
  }, [store.rbac, t]);

  const onEditMember = useCallback(
    (admin) => {
      setSelectedMember(admin);
      setOpenEditMemberDialog(true);
    },
    [setOpenEditMemberDialog],
  );

  const handleLandlord = useCallback(
    (landlord) => {
      setLandlordToEdit(landlord);
    },
    [setLandlordToEdit],
  );

  const onAddMember = useCallback(
    async (members) => {
      //create account
      try {
        const currentAdminCount = memberList.filter((member) => member.role !== LANDLORD_ROLE).length;

        // Limit to a maximum of 5 admins
        if (currentAdminCount >= 5) {
          store.pushToastMessage({
            message: t(
              "You cannot add more than 5 administrators. To increase this limit, please contact technical support.",
            ),
            severity: "error",
          });
          return;
        }
        const response = await store.user.createAdmin(
          members.fullName,
          members.email,
          members.role,
          members.scopeProperty,
          members.realmId,
        );

        if (response.status !== 200) {
          switch (response) {
            case 422:
              store.pushToastMessage({
                message: t("Some fields are missing"),
                severity: "error",
              });
              return;
            case 409:
              store.pushToastMessage({
                message: t("This user is already registered"),
                severity: "error",
              });
              return;
            default:
              store.pushToastMessage({
                message: t("Something went wrong"),
                severity: "error",
              });
              return;
          }
        } else {
          store.pushToastMessage({
            message: t("Successfully added members and email password reset settings have been sent"),
            severity: "success",
          });
        }

        let newStructurMember = {
          email: `${response.data.email}`,
          name: `${response.data.fullName}`,
          role: `${response.data.role}`,
          userId: `${response.data._id}`,
        };

        const updatedMembers = [...store.organization.selected.members, newStructurMember];

        await onSubmit({
          members: updatedMembers,
        });

        // Refresh the member list
        await store.user.fetch(store.organization?.selected._id);
      } catch (error) {
        console.error(error);
        store.pushToastMessage({
          message: t("Something went wrong"),
          severity: "error",
        });
      }

      await router.push(`/settings`);
    },
    [onSubmit, router, store, t],
  );

  // Handle Dialog
  const handleRemove = useCallback(
    (member) => {
      setMemberToRemove(member);
    },
    [setMemberToRemove],
  );

  const handleDisable = useCallback(
    (member) => {
      setMemberToDisable(member);
    },
    [setMemberToDisable],
  );

  const handleEnable = useCallback(
    (member) => {
      setMemberToEnable(member);
    },
    [setMemberToEnable],
  );

  const handleSend = useCallback(
    (member) => {
      setMemberToSend(member);
    },
    [setMemberToSend],
  );

  // Handle Submit
  const handleRemoveMember = useCallback(async () => {
    if (!memberToRemove) {
      return;
    }

    const realmData = store.organization.selected.members;
    const newRealmData = realmData.filter((item) => item.userId !== memberToRemove._id);

    const organization = {
      ...store.organization.selected,
      members: newRealmData,
    };

    const responseData = await store.organization.update(organization);

    if (responseData.status !== 200) {
      switch (responseData.status) {
        case 422:
          return store.pushToastMessage({
            message: t("Some fields are missing"),
            severity: "error",
          });
        case 403:
          return store.pushToastMessage({
            message: t("You are not allowed to update the settings"),
            severity: "error",
          });
        case 409:
          return store.pushToastMessage({
            message: t("The organization name already exists"),
            severity: "error",
          });
        default:
          return store.pushToastMessage({
            message: t("Something went wrong"),
            severity: "error",
          });
      }
    } else {
      store.organization.setSelected(responseData.data, store.user);
      store.organization.setItems([
        ...store.organization.items.filter(({ _id }) => _id !== responseData.data._id),
        responseData.data,
      ]);
    }

    const response = await store.user.removeAdmin(memberToRemove._id);

    if (response.status !== 200) {
      switch (response.status) {
        default:
          store.pushToastMessage({
            message: t("Something went wrong"),
            severity: "error",
          });
          break;
      }
    } else {
      store.pushToastMessage({
        message: t("Success remove member"),
        severity: "success",
      });

      // Refresh the member list
      await store.user.fetch(store.organization?.selected._id);
    }
  }, [memberToRemove, store, t]);

  const handleDisableMember = useCallback(async () => {
    if (!memberToDisable) {
      return;
    }

    const disableAdmin = {
      ...memberToDisable,
      isEnabled: false,
      scopeProperty: memberToDisable.scopeProperty.map((item) => item.id),
    };

    const response = await store.user.updateUser(
      disableAdmin._id,
      disableAdmin.email,
      disableAdmin.fullName,
      disableAdmin.role,
      disableAdmin.isEnabled,
      disableAdmin.scopeProperty,
    );
    if (response.status !== 200) {
      switch (response.status) {
        default:
          store.pushToastMessage({
            message: t("Something went wrong"),
            severity: "error",
          });
          break;
      }
    } else {
      store.pushToastMessage({
        message: t("Success disable member"),
        severity: "success",
      });
    }

    const newUpdatedMembers = updatedMembers.map((existingMember) => {
      if (existingMember._id === memberToDisable._id) {
        return {
          ...existingMember,
          isEnabled: false,
        };
      } else {
        return existingMember;
      }
    });
    setUpdatedMembers(newUpdatedMembers);
  }, [memberToDisable, store, t]);

  const handleEnableMember = useCallback(async () => {
    if (!memberToEnable) {
      return;
    }

    const enableAdmin = {
      ...memberToEnable,
      isEnabled: true,
      scopeProperty: memberToEnable.scopeProperty.map((item) => item.id),
    };

    const response = await store.user.updateUser(
      enableAdmin._id,
      enableAdmin.email,
      enableAdmin.fullName,
      enableAdmin.role,
      enableAdmin.isEnabled,
      enableAdmin.scopeProperty,
    );
    if (response.status !== 200) {
      switch (response.status) {
        default:
          store.pushToastMessage({
            message: t("Something went wrong"),
            severity: "error",
          });
          break;
      }
    } else {
      store.pushToastMessage({
        message: t("Success enable member"),
        severity: "success",
      });
    }

    const newUpdatedMembers = updatedMembers.map((existingMember) => {
      if (existingMember._id === memberToEnable._id) {
        return {
          ...existingMember,
          isEnabled: true,
        };
      } else {
        return existingMember;
      }
    });
    setUpdatedMembers(newUpdatedMembers);
  }, [memberToEnable, store, t]);

  const handleSendEmail = useCallback(async () => {
    if (!memberToSend) {
      return;
    }
    try {
      const status = await store.user.forgotPassword(memberToSend.email);

      if (status !== 200) {
        switch (status) {
          case 422:
            store.pushToastMessage({
              message: t("Some fields are missing"),
              severity: "error",
            });
            return;
          case 409:
            store.pushToastMessage({
              message: t("Password reset cannot be performed on accounts managed by Google login"),
              severity: "error",
            });
            return;
          default:
            store.pushToastMessage({
              message: t("Something went wrong"),
              severity: "error",
            });
            return;
        }
      } else {
        store.pushToastMessage({
          message: t("Successfully send email password reset settings"),
          severity: "success",
        });
      }
    } catch (error) {
      console.error(error);
      store.pushToastMessage({
        message: t("Something went wrong"),
        severity: "error",
      });
    }
  }, [memberToSend, store, t]);

  return (
    <Section label={t("Your Member")}>
      <Box py={2}>
        <FormDialog members={updatedMembers} onSubmit={onAddMember} />
      </Box>
      <Hidden smDown>
        <Paper variant="outlined" square>
          <Table aria-label="member table">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <Typography>{t("Name")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{t("Email")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{t("Role")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{t("Property")}</Typography>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {updatedMembers
                .filter((member) => member.role === "landlord")
                .map((landlord, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell align="center">
                        <PersonIcon />
                      </TableCell>
                      <TableCell>
                        <Typography>{landlord.fullName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{landlord.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{roleList[landlord.role]}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{t("All property")}</Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleLandlord(landlord)}
                          disabled={landlord.provider == "Google" ? true : false}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Button
                          style={{ marginRight: "10px" }}
                          size="small"
                          variant="contained"
                          onClick={() => handleSend(landlord)}
                        >
                          {t("Reset Password")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {updatedMembers
                .filter((member) => member.role !== "landlord")
                .map((admin, index) => (
                  <TableRow key={index} style={{ backgroundColor: admin.isEnabled ? "inherit" : "#f5f5f5" }}>
                    <TableCell align="center">
                      <SupervisorAccountIcon style={{ color: admin.isEnabled ? "inherit" : "#bdbdbd" }} />
                    </TableCell>
                    <TableCell>
                      <Typography style={{ color: admin.isEnabled ? "inherit" : "#bdbdbd" }}>
                        {admin.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography style={{ color: admin.isEnabled ? "inherit" : "#bdbdbd" }}>{admin.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography style={{ color: admin.isEnabled ? "inherit" : "#bdbdbd" }}>
                        {roleList[admin.role]}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {admin.role === "administrator" ? (
                        admin.scopeProperty.map((property, index) => (
                          <Typography key={index} style={{ color: admin.isEnabled ? "inherit" : "#bdbdbd" }}>
                            {property.name}
                          </Typography>
                        ))
                      ) : (
                        <Typography style={{ color: admin.isEnabled ? "inherit" : "#bdbdbd" }}>
                          {t("All property")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton disabled={!admin.isEnabled} onClick={() => onEditMember(admin)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleRemove(admin)} disabled={!admin.isEnabled}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <Button
                        style={{ marginRight: "10px" }}
                        disabled={!admin.isEnabled}
                        size="small"
                        variant="contained"
                        onClick={() => handleSend(admin)}
                      >
                        {t("Reset Password")}
                      </Button>
                      {admin.isEnabled ? (
                        <Button size="small" variant="contained" onClick={() => handleDisable(admin)}>
                          {t("Disable")}
                        </Button>
                      ) : (
                        <Button size="small" variant="contained" onClick={() => handleEnable(admin)}>
                          {t("Enable")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Paper>
      </Hidden>
      <Hidden mdUp>
        <MemberGrid
          members={updatedMembers}
          onRemove={handleRemove}
          onDisable={handleDisable}
          onEnable={handleEnable}
          onSend={handleSend}
          onEditLandlord={handleLandlord}
          onEditAdmin={onEditMember}
        />
      </Hidden>

      {/* <EditLandlordDialog
        memberData={selectedLandlord}
        backPage={t('Settings')}
      /> */}
      <EditLandlordDialog title="Edit landlord?" onSubmit={onSubmit} />
      <EditMemberDialog memberData={selectedMember} backPage={t("Settings")} />

      <ConfirmRemoveDialog
        title={t("Are you sure to remove this member?")}
        subTitle={memberToRemove.fullName}
        onConfirm={handleRemoveMember}
      />
      <ConfirmDisableDialog
        title={t("Are you sure to disable this member?")}
        subTitle={memberToDisable.fullName}
        onConfirm={handleDisableMember}
      />
      <ConfirmEnableDialog
        title={t("Are you sure to enable this member?")}
        subTitle={memberToEnable.fullName}
        onConfirm={handleEnableMember}
      />
      <ConfirmSendDialog
        title={t("Are you sure to request reset password for this member?")}
        subTitle={memberToSend.email}
        onConfirm={handleSendEmail}
      />
    </Section>
  );
});

export default Members;
